import {
  Context,
  createContext,
  PropsWithChildren,
  useContext,
  useState,
} from "react";

import {ActionRecordModel, ActionTypeModel} from "@/api/models";
import useActionListManage from "@/hooks/useActionListManage";

export type ActionListContextValue<T extends ActionRecordModel> = ReturnType<
  typeof useActionListManage<T>
> & {
  search: string;
  setSearch: (search: string) => void;
};

export const ActionListContext =
  createContext<ActionListContextValue<ActionRecordModel> | null>(null);

export const useActionListContext = <
  T extends ActionRecordModel = ActionRecordModel,
>(
  strict = true,
) => {
  const context = useContext<ActionListContextValue<T> | null>(
    ActionListContext as unknown as Context<ActionListContextValue<T> | null>,
  );

  if (strict && !context) {
    throw new Error(
      "useActionListContext must be used within a ActionListContextProvider",
    );
  }

  return context;
};

export type ActionListContextProviderProps = PropsWithChildren & {
  actionType: ActionTypeModel;
};

export const ActionListContextProvider = <
  T extends ActionRecordModel = ActionRecordModel,
>(
  props: ActionListContextProviderProps,
) => {
  const {children, actionType} = props;
  const [search, setSearch] = useState("");

  const actionListManage = useActionListManage<T>({actionType, search});

  const contextValue: ActionListContextValue<T> = {
    ...actionListManage,
    search,
    setSearch,
  };

  return (
    <ActionListContext.Provider value={contextValue}>
      {children}
    </ActionListContext.Provider>
  );
};
